import {
    Description,
    Dialog as HeadlessDialog,
    DialogPanel,
    DialogTitle,
    Transition,
    TransitionChild
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import * as React from 'react';

interface DialogContextValue {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
}

const DialogContext = React.createContext<DialogContextValue | null>(null);

interface DialogProps {
    children?: React.ReactNode;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

function Dialog({ children, open, onOpenChange }: DialogProps) {
    const [
        internalOpen,
        setInternalOpen,
    ] = React.useState(false);
    const isControlled = open !== undefined;
    const isOpen = isControlled ? open : internalOpen;
    const setIsOpen = isControlled
        ? (next: boolean) => {
            onOpenChange?.(next);
        }
        : setInternalOpen;

    return (
        <DialogContext.Provider value={ { isOpen, setIsOpen } }>
            {children}
        </DialogContext.Provider>
    );
}

function useDialogContext(): DialogContextValue {
    const context = React.useContext(DialogContext);

    if (!context) {
        throw new Error('Dialog components must be used within a Dialog');
    }
    return context;
}

/**
 * Whether the calling component renders inside a `<Dialog>`. Form leaves use
 * this to portal their dropdown menus automatically, so the menus aren't
 * clipped by the dialog's `overflow-hidden`/`overflow-y-auto` ancestors.
 * Works through the Headless UI portal because React context follows the
 * component tree, not the DOM.
 */
function useInsideDialog(): boolean {
    return React.useContext(DialogContext) !== null;
}

interface DialogTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    children?: React.ReactNode;
    asChild?: boolean;
    className?: string;
}

const DialogTrigger = React.forwardRef<HTMLButtonElement, DialogTriggerProps>(
    ({ children, asChild, className, ...props }, ref) => {
        const { setIsOpen } = useDialogContext();

        if (asChild && React.isValidElement(children)) {
            const child = children as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>;

            return React.cloneElement(child, {
                ...props,
                ref,
                onClick: (e: React.MouseEvent) => {
                    child.props.onClick?.(e);
                    setIsOpen(true);
                },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any);
        }

        return (
            <button
                className={ className }
                onClick={ () => {
                    return setIsOpen(true);
                } }
                ref={ ref }
                type='button'
                { ...props }
            >
                {children}
            </button>
        );
    }
);

DialogTrigger.displayName = 'DialogTrigger';

interface DialogCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    children?: React.ReactNode;
    asChild?: boolean;
    className?: string;
}

const DialogClose = React.forwardRef<HTMLButtonElement, DialogCloseProps>(
    ({ children, asChild, className, ...props }, ref) => {
        const { setIsOpen } = useDialogContext();

        if (asChild && React.isValidElement(children)) {
            const child = children as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>;

            return React.cloneElement(child, {
                ...props,
                ref,
                onClick: (e: React.MouseEvent) => {
                    child.props.onClick?.(e);
                    setIsOpen(false);
                },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any);
        }

        return (
            <button
                className={ className }
                onClick={ () => {
                    return setIsOpen(false);
                } }
                ref={ ref }
                type='button'
                { ...props }
            >
                {children}
            </button>
        );
    }
);

DialogClose.displayName = 'DialogClose';

interface DialogContentProps {
    className?: string;
    children?: React.ReactNode;
}

const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
    ({ className, children }, ref) => {
        const { isOpen, setIsOpen } = useDialogContext();

        return (
            <Transition as={ React.Fragment } show={ isOpen }>
                <HeadlessDialog
                    as='div'
                    className='relative z-50'
                    onClose={ () => {
                        return setIsOpen(false);
                    } }
                >
                    {/* Overlay */}
                    <TransitionChild
                        as={ React.Fragment }
                        enter='ease-out duration-300'
                        enterFrom='opacity-0'
                        enterTo='opacity-100'
                        leave='ease-in duration-200'
                        leaveFrom='opacity-100'
                        leaveTo='opacity-0'
                    >
                        <div className='fixed inset-0 bg-black/80' />
                    </TransitionChild>

                    {/* Content */}
                    <div className='fixed inset-0 overflow-y-auto'>
                        <div className='flex min-h-full items-center justify-center p-4'>
                            <TransitionChild
                                as={ React.Fragment }
                                enter='ease-out duration-300'
                                enterFrom='opacity-0 scale-95'
                                enterTo='opacity-100 scale-100'
                                leave='ease-in duration-200'
                                leaveFrom='opacity-100 scale-100'
                                leaveTo='opacity-0 scale-95'
                            >
                                <DialogPanel
                                    className={ clsx(
                                        'relative w-full max-w-lg max-h-[90vh] overflow-hidden rounded-lg border bg-background shadow-lg transition-all flex flex-col',
                                        className
                                    ) }
                                    ref={ ref }
                                >
                                    {children}
                                    <button
                                        className='absolute right-3 top-3 z-50 rounded-full p-1.5 text-foreground bg-background border border-border shadow-sm ring-offset-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
                                        onClick={ () => {
                                            return setIsOpen(false);
                                        } }
                                        type='button'
                                    >
                                        <XMarkIcon className='h-4 w-4' />
                                        <span className='sr-only'>Close</span>
                                    </button>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </HeadlessDialog>
            </Transition>
        );
    }
);

DialogContent.displayName = 'DialogContent';

interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
    children?: React.ReactNode;
    className?: string;
}

function DialogHeader({ className, children, ...props }: DialogHeaderProps) {
    if (children === null || children === undefined || children === false) {
        return null;
    }

    return (
        <div
            className={ clsx(
                'sticky top-0 z-10 flex flex-col space-y-1.5 text-center sm:text-left px-6 pt-6 pb-4 border-b border-border bg-background',
                className
            ) }
            { ...props }
        >
            {children}
        </div>
    );
}

DialogHeader.displayName = 'DialogHeader';

interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {
    className?: string;
}

function DialogFooter({ className, ...props }: DialogFooterProps) {
    return (
        <div
            className={ clsx(
                'sticky bottom-0 z-10 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 px-6 py-4 border-t border-border bg-background',
                className
            ) }
            { ...props }
        />
    );
}

DialogFooter.displayName = 'DialogFooter';

interface DialogTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
    className?: string;
}

const DialogTitleComponent = React.forwardRef<HTMLHeadingElement, DialogTitleProps>(
    ({ className, ...props }, ref) => {
        return (
            <DialogTitle
                className={ clsx('text-lg font-semibold leading-none tracking-tight', className) }
                ref={ ref }
                { ...props }
            />
        );
    }
);

DialogTitleComponent.displayName = 'DialogTitle';

interface DialogDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
    className?: string;
    as?: React.ElementType;
}

const DialogDescription = React.forwardRef<HTMLParagraphElement, DialogDescriptionProps>(
    ({ className, ...props }, ref) => {
        return (
            <Description
                className={ clsx('text-sm text-muted-foreground', className) }
                ref={ ref }
                { ...props }
            />
        );
    }
);

DialogDescription.displayName = 'DialogDescription';

// Compatibility exports
function DialogPortal({ children }: { children?: React.ReactNode }) {
    return <>{children}</>;
}

function DialogOverlay() {
    return null;
}

export {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitleComponent as DialogTitle,
    DialogTrigger,
    useInsideDialog
};
